import React from 'react';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { RuleCompareOperator } from '@bringg/types';

import { MultiSelectOption } from '../../../components/multi-select/multi-select';
import { Numeric, NumericTranslations } from '../numeric';
import { BooleanTranslations } from '../boolean/boolean-condition';
import { FreeText, FreeTextTranslations } from '../free-text';
import { ArrayTranslations } from '../array/array-condition';

export interface Condition {
	id: number;
	name: string;
	path: string;
	value: boolean[] | string | string[] | number[] | Numeric[] | FreeText[];
	operator?: RuleCompareOperator;
	type: ConditionType;
	options?: Partial<MultiSelectOption>[];
	translations?: NumericTranslations | BooleanTranslations | FreeTextTranslations | ArrayTranslations;
}

export interface Attribute {
	type?: ConditionType;
	title: string;
	disabled?: boolean;
	options?: Partial<MultiSelectOption>[];
	attributes?: Attribute[];
	path?: string;
	display_path: string;
	identifier: string;
}

export enum ConditionType {
	NUMBER = 'NUMBER',
	BOOLEAN = 'BOOLEAN',
	LIST = 'LIST',
	STRING = 'STRING',
	ARRAY = 'ARRAY',
	DATETIME = 'DATETIME',
	STRING_ARRAY = 'STRING_ARRAY',
	NUMBER_ARRAY = 'NUMBER_ARRAY',
	BOOLEAN_ARRAY = 'BOOLEAN_ARRAY'
}

export const trashIcon = <BringgIcon iconName={BringgFontIcons.Trash} />;
export const plusIcon = <BringgIcon iconName={BringgFontIcons.Plus} />;
export const copyIcon = <BringgIcon iconName={BringgFontIcons.Copy} />;
export const menuIcon = <BringgIcon iconName={BringgFontIcons.Menu} />;

export const FALLBACK_TEST_ID = 'condition_default_test_id';
const generateGuid = () => {
	let counter = 1;
	return (): number => {
		return counter++;
	};
};
export const getRandomGuid = generateGuid();

export const getEmptyCondition = ({ name, type, path, onChange, onRemove, index, options }): Condition => {
	const defaultProps = {
		id: getRandomGuid(),
		name,
		type,
		path,
		onChange,
		onRemove,
		index
	};
	switch (type) {
		case ConditionType.BOOLEAN:
			return {
				...defaultProps,
				value: [false]
			};
		case ConditionType.LIST:
		case ConditionType.ARRAY:
			return {
				...defaultProps,
				value: [],
				options: options || []
			};
		case ConditionType.STRING:
		case ConditionType.NUMBER:
			return {
				...defaultProps,
				value: []
			};
		default:
			return null;
	}
};

export const getConditionTestId = (path: string): string => {
	return typeof path === 'string' ? path.replace(/\s+/g, '_').toLowerCase() : FALLBACK_TEST_ID;
};

export const findAttribute = (attributes: Attribute[], path: string) => {
	let result;
	for (let attr of attributes) {
		if (attr.path === path) {
			return attr;
		}
		result = attr.attributes && findAttribute(attr.attributes, path);
		if (result) {
			return result;
		}
	}
	return null;
};

export const toggleAttrOptions = (isMatch, override: Partial<Attribute>, list: Attribute[]): Attribute[] =>
	list.map(attr => ({
		...attr,
		...(isMatch(attr) && { ...override }),
		attributes: attr.attributes?.length ? toggleAttrOptions(isMatch, override, attr.attributes) : undefined
	}));
